// services/placesService.js
//
// Venue search, through the `places-search` Edge Function.
//
// ── The session token is the whole cost story ──────────────────────────────
//
// Google bills Places autocomplete per SESSION rather than per request, but
// only when every request in that session carries the same token and the
// session is closed by a Details call. Get it wrong and each autocomplete is
// billed separately.
//
// A session here is: the host opens the venue sheet, types, presses Search
// (possibly more than once, refining), taps a result. One token spans all of
// it and is thrown away afterwards — reusing a token across two different
// searches is the other way to be billed twice.
//
// ── Why search is a button, not a keystroke ────────────────────────────────
//
// Autocomplete-as-you-type is the usual pattern and it is the expensive one:
// it opens a session on the first letter whether or not anyone ever picks a
// place, so every abandoned search costs money. Typing first and pressing
// Search means a session exists only once someone actually wants results.

import supabase from './supabase';

/**
 * The country this browser is probably in, from its own locale.
 *
 * Free, instant, and needs no permission — which is the point: it works when
 * someone has refused location access, because it never asks. It is only a
 * hint, and the lowest-priority one: the Edge Function prefers the profile's
 * own country, then the dialling code, then the IP, and falls back to this.
 *
 * `en-IN` gives IN. A bare `en` gives nothing, which is correct — a language
 * with no region attached says nothing about where anyone is.
 */
export function browserRegion() {
  try {
    const locales = [
      ...(navigator.languages ?? []),
      navigator.language,
    ].filter(Boolean);

    for (const locale of locales) {
      // Intl knows the real parse; the regex below is the fallback for
      // engines whose Locale constructor is missing.
      if (typeof Intl !== 'undefined' && typeof Intl.Locale === 'function') {
        const region = new Intl.Locale(locale).region;
        if (region && /^[A-Z]{2}$/.test(region)) return region;
      }
      const match = /[-_]([A-Za-z]{2})(?:\b|$)/.exec(locale);
      if (match) return match[1].toUpperCase();
    }
  } catch {
    // A missing or odd navigator is not worth an exception over.
  }
  return null;
}

/**
 * A session token: a UUID v4, which is what Google's docs specify.
 *
 * crypto.randomUUID is not in older Safari, and a venue search that throws on
 * an iPhone two versions back is worse than a slightly less elegant fallback.
 */
function newSessionToken() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * One search session. Make one when the venue sheet opens, and throw it away
 * once a place has been chosen.
 */
export function createPlacesSession() {
  let token = newSessionToken();

  return {
    get token() { return token; },

    /** Start over — after a pick, so the next search is its own session. */
    renew() { token = newSessionToken(); },

    /**
     * @param {string} query
     * @param {string|null} region ISO alpha-2, used only if the profile has none
     * @returns {Promise<Array<{placeId: string, mainText: string, secondaryText: string}>>}
     */
    async search(query, region = browserRegion()) {
      // Guarded here as well as in the function. The function would answer an
      // empty list anyway, but a round trip that cannot return anything is a
      // round trip not worth making.
      if (!query || query.trim().length < 2) return [];
      const q = query.trim();

      // ── The cache, first ────────────────────────────────────────────────
      //
      // Venues chosen before are already in the `places` table, and the app
      // has searched it first since Places was added there. The website was
      // going straight to Google — paying for answers it already had.
      //
      // A hit here costs nothing and does not open a billable session at all.
      try {
        const { data: cached } = await supabase.rpc('search_places', {
          search_query: q,
          limit_count: 5,
        });

        if (Array.isArray(cached) && cached.length > 0) {
          return cached.map((place) => ({
            placeId: place.place_id,
            mainText: place.display_name,
            secondaryText: place.formatted_address,
            // Carried through so picking one needs no Details call either.
            cached: {
              placeId: place.place_id,
              name: place.display_name,
              address: place.formatted_address,
              lat: place.lat,
              lng: place.lng,
            },
          }));
        }
      } catch (err) {
        // The cache is an optimisation. If it is unavailable, pay Google.
        console.warn('Place cache lookup failed:', err?.message);
      }

      const { data, error } = await supabase.functions.invoke('places-search', {
        body: { action: 'autocomplete', query: q, sessionToken: token, region },
      });

      if (error) throw new Error('Could not reach venue search.');
      if (data?.error) throw new Error(data.error);

      return data?.suggestions ?? [];
    },

    /**
     * Closes the session and returns the venue.
     *
     * @returns {Promise<{placeId, name, address, lat, lng}>}
     */
    async details(placeId, cached = null) {
      // A cached suggestion already carries everything Details would return,
      // so picking one costs nothing and never opens a session.
      if (cached) return cached;

      const { data, error } = await supabase.functions.invoke('places-search', {
        body: { action: 'details', placeId, sessionToken: token },
      });

      if (error) throw new Error('Could not load that place.');
      if (data?.error) throw new Error(data.error);

      // Cached for everyone after them. Not awaited — a cache write must not
      // slow down picking a venue, and a failed write costs only one repeat
      // lookup later.
      //
      // Wrapped, not just `.then(null, …)`: the throw to guard against is the
      // synchronous one from building the query, not the rejection. Losing the
      // cache write is a missed optimisation; losing the venue the host just
      // chose, because writing it down failed, would be absurd.
      try {
        const write = supabase
          .from('places')
          .upsert(
            {
              place_id: data.place.placeId,
              display_name: data.place.name,
              formatted_address: data.place.address,
              lat: data.place.lat,
              lng: data.place.lng,
              search_text: `${data.place.name} ${data.place.address}`,
            },
            { onConflict: 'place_id' },
          );
        if (write && typeof write.then === 'function') {
          write.then(null, (err) => console.warn('Could not cache place:', err?.message));
        }
      } catch (err) {
        console.warn('Could not cache place:', err?.message);
      }

      // The session is spent whether or not the host keeps this venue.
      token = newSessionToken();

      return data.place;
    },
  };
}

/**
 * The embedded map for a chosen venue.
 *
 * The Maps Embed API is free and unmetered, so this key is the one piece of
 * Google configuration that can safely live in the browser bundle — there is
 * no quota to burn. Restrict it to the Maps Embed API and to this site's
 * referrers anyway; free is not the same as "let anyone put it on their page".
 *
 * `place_id:` rather than coordinates: it shows Google's own pin and label for
 * the venue instead of a dropped marker in a field.
 */
export function embedMapUrl({ placeId, lat, lng, name }) {
  const key = process.env.REACT_APP_GOOGLE_MAPS_EMBED_KEY;
  if (!key) return null;

  const base = 'https://www.google.com/maps/embed/v1/place';

  const q = placeId
    ? `place_id:${placeId}`
    : (lat != null && lng != null)
      ? `${lat},${lng}`
      : name;

  if (!q) return null;

  return `${base}?key=${encodeURIComponent(key)}&q=${encodeURIComponent(q)}`;
}

/** Is venue search available on this deployment? */
export function isPlacesConfigured() {
  // The Places key lives in Edge Function secrets, so the browser cannot see
  // whether it is set — the function answers that. What the browser can check
  // is that it is signed in, which is the gate that matters here.
  return true;
}
